import { useEffect, useState } from 'react';
import {
  BRANCH_CODES,
  BRANCH_LABELS,
  SUPPLY_TYPES,
  type StockTarget,
  type Supplier,
  type SupplyType,
} from '@lorsamthai/ops-shared';
import {
  applySupplierAcrossBranches,
  listSuppliers,
  listTargetsForSku,
  updateStockTarget,
} from '../lib/queries.js';
import { fmtInt } from '../lib/format.js';

export default function TargetsEditor({ sku, onSaved }: { sku: string; onSaved?: () => void }) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [rows, setRows] = useState<StockTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingBranch, setSavingBranch] = useState<string | null>(null);
  const [bulkSupplierId, setBulkSupplierId] = useState<number | ''>('');
  const [applying, setApplying] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, s] = await Promise.all([listTargetsForSku(sku), listSuppliers()]);
      setRows(t.items.sort((a, b) => a.branch.localeCompare(b.branch)));
      setSuppliers(s.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  const setRow = (branch: string, patch: Partial<StockTarget>) =>
    setRows((prev) => prev.map((r) => (r.branch === branch ? { ...r, ...patch } : r)));

  const saveRow = async (row: StockTarget) => {
    setSavingBranch(row.branch);
    setOkMsg(null);
    try {
      await updateStockTarget(row.sku, row.branch, {
        min_qty: row.min_qty,
        target_qty: row.target_qty,
        safety_days: row.safety_days,
        supply_type: row.supply_type,
        primary_supplier_id: row.primary_supplier_id,
        auto_calc: row.auto_calc,
      });
      setOkMsg(`บันทึก ${BRANCH_LABELS[row.branch]} แล้ว`);
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'save failed');
    } finally {
      setSavingBranch(null);
    }
  };

  const applyBulkSupplier = async () => {
    setApplying(true);
    setOkMsg(null);
    try {
      const id = bulkSupplierId === '' ? null : Number(bulkSupplierId);
      const r = await applySupplierAcrossBranches(sku, id);
      setOkMsg(`ตั้ง supplier ให้ ${r.updated} สาขาแล้ว`);
      await load();
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed');
    } finally {
      setApplying(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">กำลังโหลดนโยบาย...</div>;
  if (error) return <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold">นโยบาย stock + supplier</h2>
        {okMsg && <span className="text-xs text-emerald-700">{okMsg}</span>}
      </div>

      <div className="rounded-lg bg-slate-50 p-3">
        <div className="text-xs text-slate-500">ตั้ง Supplier หลักให้ทั้ง 4 สาขาในครั้งเดียว</div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            value={bulkSupplierId}
            onChange={(e) => setBulkSupplierId(e.target.value ? Number(e.target.value) : '')}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm"
          >
            <option value="">— เลือก —</option>
            <option value={0}>เคลียร์ supplier</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={applyBulkSupplier}
            disabled={bulkSupplierId === '' || applying}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {applying ? '...' : 'Apply ทุกสาขา'}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">สาขา</th>
              <th className="px-3 py-2 text-right">Min</th>
              <th className="px-3 py-2 text-right">Target</th>
              <th className="px-3 py-2 text-right">Safety days</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Supplier</th>
              <th className="px-3 py-2 text-center">Auto</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {BRANCH_CODES.map((bc) => {
              const row = rows.find((r) => r.branch === bc);
              if (!row) return null;
              const disabled = row.auto_calc;
              return (
                <tr key={row.branch}>
                  <td className="px-3 py-2 text-xs">{BRANCH_LABELS[row.branch]}</td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number" min={0}
                      value={row.min_qty}
                      onChange={(e) => setRow(row.branch, { min_qty: Number(e.target.value) })}
                      disabled={disabled}
                      className="w-20 rounded-md border border-slate-200 px-2 py-1 text-right disabled:bg-slate-50"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number" min={1}
                      value={row.target_qty}
                      onChange={(e) => setRow(row.branch, { target_qty: Number(e.target.value) })}
                      disabled={disabled}
                      className="w-20 rounded-md border border-slate-200 px-2 py-1 text-right disabled:bg-slate-50"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number" min={0}
                      value={row.safety_days}
                      onChange={(e) => setRow(row.branch, { safety_days: Number(e.target.value) })}
                      className="w-16 rounded-md border border-slate-200 px-2 py-1 text-right"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.supply_type}
                      onChange={(e) => setRow(row.branch, { supply_type: e.target.value as SupplyType })}
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
                    >
                      {SUPPLY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.primary_supplier_id ?? ''}
                      onChange={(e) =>
                        setRow(row.branch, {
                          primary_supplier_id: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                      className="max-w-[180px] rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
                    >
                      <option value="">—</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={row.auto_calc}
                      onChange={(e) => setRow(row.branch, { auto_calc: e.target.checked })}
                      title="ให้ระบบคำนวณ min/target ตอน cron"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => saveRow(row)}
                      disabled={savingBranch === row.branch}
                      className="rounded-md bg-slate-900 px-2 py-1 text-xs text-white disabled:opacity-60"
                    >
                      {savingBranch === row.branch ? '...' : 'บันทึก'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        💡 <strong>auto_calc = ติ๊ก</strong> → cron จะคำนวณ min/target ให้อัตโนมัติจาก velocity (ปลด tick = ตั้งเอง คงค่า)
        <br/>
        velocity ปัจจุบัน:{' '}
        {rows.map((r) => (
          <span key={r.branch} className="ml-2 inline-block">
            {BRANCH_LABELS[r.branch]} {Number(r.avg_daily_sales).toFixed(2)}/วัน
          </span>
        ))}
      </p>
    </section>
  );
}
